import * as Actions from '@/redux/actions';
import * as Constants from '@/redux/constants';
import { useDialogs } from '@toolpad/core';
import { useDispatch, useSelector } from 'hooks';
import { convertEmptyStringToNull, dateToString, uncapitalize } from 'lib/utils';
import { useI18n } from 'locales';
import get from 'lodash/get';
import snakeCase from 'lodash/snakeCase';
import toLower from 'lodash/toLower';
import toUpper from 'lodash/toUpper';
import * as Models from 'models';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray as useReactHookFieldArray, useForm as useReactHookForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';

const defaultValueReducer = (Model, acc, row) => {
  if (typeof row === 'string') {
    row = [row];
  }

  for (const column of row) {
    const field = Model.fields[column];

    if (field.type == 'table') {
      continue;
    }

    if (field.defaultValue) {
      acc[field.name] = field.defaultValue;
    } else {
      switch (field.type) {
        case 'text':
        case 'textarea':
          acc[field.name] = '';
          break;
        default:
          acc[field.name] = null;
          break;
      }
    }
  }
  return acc;
};

function useForm({
  model,
  id,
  formType = 'edit',
  payload = {},
}) {
  const dialogs = useDialogs();
  const { itemInfo, optionsItemInfo } = useSelector((state) => ({
    itemInfo: get(state, `${uncapitalize(model)}Info`),
    optionsItemInfo: get(state, `options${model}Info`),
  }), shallowEqual);
  const t = useI18n();
  const dispatch = useDispatch();
  const { control, formState: { errors, isDirty }, handleSubmit, reset, watch, getValues, setValue, setError } = useReactHookForm({
    // reValidateMode: 'onBlur',
    defaultValues: Models[model].form[formType].reduce(defaultValueReducer.bind(null, Models[model]), {}),
  });
  const _optionFilters = watch(Models[model].optionFilters || []);
  const optionFilters = useMemo(() => {
    return JSON.stringify(_optionFilters.reduce((acc, value, i) => {
      acc[Models[model].optionFilters[i]] = value;
      return acc;
    }, {}));
  }, [_optionFilters]);
  const fieldArrays = Models[model].form[formType].reduce((acc, row) => {
    if (typeof row === 'string') {
      row = [row];
    }

    for (const column of row) {
      const field = Models[model].fields[column];
      if (field.type === 'table') {
        acc[field.name] = useReactHookFieldArray({
          control,
          name: field.name,
        });
      }
    }
    return acc;
  }, {});
  const isFetching = itemInfo.isFetching;
  const fields = useMemo(() => {
    const ret = [];
    let rows = Models[model].form[formType];

    if (model === 'user' && payload && payload === 'profile') {
      rows = Models[model].form.profile;
    }

    for (const row of rows) {
      // if row is string
      if (typeof row === 'string') {
        ret.push(Models[model].fields[row]);
      } else {
        const tmp = [];
        for (const field of row) {
          tmp.push(Models[model].fields[field]);
        }
        ret.push(tmp);
      }
    }

    return ret;
  }, [model, formType]);

  const collectFormValidationErrors = useCallback((obj, path = '') => {
    const messages = [];

    if (obj && typeof obj === 'object') {
      // If this is an error object with a message
      if (obj.message) {
        const fullPath = path ? `${path}: ` : '';
        messages.push(`${fullPath} ${t(obj.message)}. `);
      }
      // If it's an array (field array errors)
      else if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          const newPath = path ? `${path}.row${index + 1}` : `row${index + 1}`;
          messages.push(...collectFormValidationErrors(item, newPath));
        });
      }
      // If it's a nested object, recurse into it
      else {
        Object.entries(obj).forEach(([key, value]) => {
          const newPath = path ? `${path}.${t(key)}` : t(get(Models, `${model}.fields.${key}.label`, key));
          messages.push(...collectFormValidationErrors(value, newPath));
        });
      }
    }

    return messages;
  }, [model, t]);

  useEffect(() => {
    if (id) {
      dispatch(Actions[`read${model}`]({ payload: { ...payload, id } }));
    } else if (model) {
      reset(Models[model].form[formType].reduce(defaultValueReducer.bind(null, Models[model]), {}));
    }
    return () => {
      if (Actions[`clearUpdate${model}`]) {
        dispatch(Actions[`clearUpdate${model}`]());
      }
    };
  }, [dispatch, id, model, reset, formType]);

  useEffect(() => {
    if (Actions[`readOptions${model}`]) {
      dispatch(Actions[`readOptions${model}`]());
    }
  }, [dispatch, model]);

  useEffect(() => {
    if (Actions[`readOptions${model}`]) {
      dispatch(Actions[`readOptions${model}`]({
        payload: JSON.parse(optionFilters),
      }));
    }
  }, [optionFilters]);

  useEffect(() => {
    const con = toUpper(snakeCase(model));
    if (!get(Constants, `READ_${con}.SUCCESS`)) {
      return;
    }
    switch (itemInfo.type) {
      case Constants[`READ_${con}`].SUCCESS:
        if (itemInfo.data.id === id) {
          const defaultValues = {};
          for (let row of fields) {
            if (!Array.isArray(row)) {
              row = [row];
            }

            const setDefaultValue = (name, field, _defaultValues = null) => {
              if (!_defaultValues) {
                _defaultValues = defaultValues;
              }
              switch (field.type) {
                case 'select':
                  if (field.multiple) {
                    _defaultValues[name] = _defaultValues[name].map((item) => item.id);
                  }
                  break;
                case 'date':
                case 'datetime':
                  _defaultValues[name] = _defaultValues[name] ? moment(_defaultValues[field.name]) : null;
                  break;
              }
            };

            for (const field of row) {
              let name = field.name;

              if (field.type === 'table') {
                defaultValues[name] = [];
                for (let i = 0; i < itemInfo.data[field.name].length; i++) {
                  defaultValues[name].push({});
                  for (const column of field.columns) {
                    defaultValues[name][i][column.name] = get(itemInfo.data, `${name}.${i}.${column.name}`);
                    setDefaultValue(name, field, defaultValues[name][i]);
                  }
                }
              } else {
                defaultValues[name] = get(itemInfo.data, name);
                setDefaultValue(name, field);
              }
            }
          }
          reset(defaultValues);
        }
        break;
    }
  }, [itemInfo, fields, id, model, reset]);

  useEffect(() => {
    const con = toUpper(snakeCase(model));
    switch (itemInfo.type) {
      case Constants[`CREATE_${con}`].SUCCESS:
      case Constants[`UPDATE_${con}`].SUCCESS:
        break;
      case Constants[`CREATE_${con}`].FAILURE:
      case Constants[`UPDATE_${con}`].FAILURE:
        for (const entry of Object.entries(get(itemInfo, 'error.errors', {}))) {
          for (const message of entry[1]) {
            setError(entry[0], { type: 'manual', message });
          }
        }
        break;
    }
  }, [itemInfo, model, setError]);

  useEffect(() => {
    const con = toUpper(snakeCase(model));
    const alert = {
      title: null,
      message: null,
    };
    const modelName = model.replace(/([A-Z])/g, ' $1').trim();
    switch (itemInfo.type) {
      /*
      case Constants[`CREATE_${con}`].SUCCESS:
        alert.title = `${modelName} Created Successfully`;
        alert.message = `The ${toLower(modelName)} is created successfully.`;
        break;
      case Constants[`UPDATE_${con}`].SUCCESS:
        if (id === itemInfo.data.id) {
          alert.title = `${modelName} Updated Successfully`;
          alert.message = `The ${toLower(modelName)} is updated successfully.`;
        }
        break;
      case Constants[`DELETE_${con}`].SUCCESS:
        alert.title = `${modelName} Deleted Successfully`;
        alert.message = `The ${toLower(modelName)} is deleted successfully.`;
        break;
      */
      case Constants[`CREATE_${con}`].FAILURE:
        alert.title = `Failed to Create ${modelName}`;
        alert.message = `Oops! Something went wrong while creating this ${toLower(modelName)}, please try again.`;
        break;
      case Constants[`UPDATE_${con}`].FAILURE:
        alert.title = `Failed to Update ${modelName}`;
        alert.message = `Oops! Something went wrong while updating this ${toLower(modelName)}, please try again.`;
        break;
      case Constants[`DELETE_${con}`].FAILURE:
        alert.title = `Failed to Delete ${modelName}`;
        alert.message = `Oops! Something went wrong while deleting this ${toLower(modelName)}, please try again.`;
        break;
    }

    if (alert.message) {
      dialogs.alert(alert.message, { title: alert.title, okText: 'OK' });
    }
  }, [itemInfo.type, model, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = useCallback((data) => {
    data = convertEmptyStringToNull({
      ...payload,
      ...data,
    });

    for (let row of fields) {
      if (!Array.isArray(row)) {
        row = [row];
      }

      for (const field of row) {
        switch (field.type) {
          case 'date':
            data[field.name] = data[field.name] ? dateToString(data[field.name]) : null;
            break;
        }
      }
    }

    if (id) {
      dispatch(Actions[`update${model}`]({
        payload: {
          id,
          ...data,
        },
      }));
    } else {
      dispatch(Actions[`create${model}`]({ payload: data }));
    }
  }, [dispatch, id, model, fields, payload]);

  const onError = useCallback((errors) => {
    const _errorMessages = collectFormValidationErrors(errors).join('\n');

    dialogs.alert(_errorMessages, { title: t('Error'), okText: t('OK') });
  }, [dialogs, t, collectFormValidationErrors]);

  return {
    itemInfo,
    optionsItemInfo,
    control,
    errors,
    isDirty,
    getValues,
    setValue,
    watch,
    fields,
    fieldArrays,
    registerOptions: Models[model].validationRules,
    isFetching,
    onSubmit: handleSubmit(onSubmit, onError),
    reset,
  };
}

useForm.propTypes = {
  model: PropTypes.string.isRequired,
  id: PropTypes.string,
  payload: PropTypes.object,
};

export default useForm;
